import { useCallback } from 'react';
import { QueryError } from '@atlassian/jira-business-error-handling/src/utils/query-error/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useExperienceAbort } from '@atlassian/jira-experience-tracker/src/ui/experience-abort/index.tsx';
import { useExperienceFail } from '@atlassian/jira-experience-tracker/src/ui/experience-fail/index.tsx';
import { showFlag } from '@atlassian/jira-flags/src/services/index.tsx';
import type { RelayAGGError } from '@atlassian/jira-horizontal-nav-error-boundary/src/ui/agg-error-boundary/utils.tsx';
import { getFlagDetails } from '../../common/get-flag-details/getFlagDetails.tsx';
import {
	HANDLED_STATUS_CODES,
	VIEW_JIRA_HORIZONTAL_NAVIGATION_EXPERIENCE,
} from '../../constants.tsx';

export const useHandleError = () => {
	const abortExperience = useExperienceAbort(VIEW_JIRA_HORIZONTAL_NAVIGATION_EXPERIENCE);
	const failExperience = useExperienceFail(VIEW_JIRA_HORIZONTAL_NAVIGATION_EXPERIENCE);

	const handleError = useCallback(
		(error: Error | QueryError | RelayAGGError) => {
			const statusCode = error && 'statusCode' in error ? Number(error.statusCode) : 0;
			const flagDetails = getFlagDetails(statusCode);

			showFlag(flagDetails);

			if (HANDLED_STATUS_CODES.includes(statusCode)) {
				abortExperience('handled client side error');
				return;
			}

			failExperience('loading horizontal navigation failed', error);
			// only fire error analytics for non-handled query errors
			if (error instanceof QueryError) {
				fireErrorAnalytics({
					meta: {
						id: 'jiraHorizontalNavigationQuery',
						packageName: 'jiraHorizontalNav',
						teamName: 'jira-cosmos',
					},
					error,
					sendToPrivacyUnsafeSplunk: true,
				});
			}
		},
		[abortExperience, failExperience],
	);

	return { handleError };
};
